import React, { useEffect, useState } from 'react';
import styled, { keyframes, css } from 'styled-components';

export interface ToastProps {
  message: string;
  type?: 'success' | 'error' | 'info' | 'warning';
  duration?: number;
  onClose: () => void;
  isVisible: boolean;
  bookingId?: number;
}

const slideIn = keyframes`
  from {
    transform: translateX(100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
`;

const slideOut = keyframes`
  from {
    transform: translateX(0);
    opacity: 1;
  }
  to {
    transform: translateX(100%);
    opacity: 0;
  }
`;

const ToastContainer = styled.div<{ $type: ToastProps['type']; $isVisible: boolean }>`
  position: fixed;
  top: 20px;
  right: 20px;
  z-index: 1000;
  min-width: 320px;
  max-width: 500px;
  padding: 16px 20px;
  border-radius: var(--border-radius-sm);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  display: flex;
  align-items: flex-start;
  gap: 12px;
  animation: ${props => props.$isVisible ? slideIn : slideOut} 0.3s ease-out;
  
  ${props => {
    switch (props.$type) {
      case 'success':
        return css`
          background-color: rgba(76, 175, 80, 0.95);
          color: white;
          border-left: 4px solid #4caf50;
        `;
      case 'error':
        return css`
          background-color: rgba(244, 67, 54, 0.95);
          color: white;
          border-left: 4px solid #f44336;
        `;
      case 'warning':
        return css`
          background-color: rgba(255, 152, 0, 0.95);
          color: white;
          border-left: 4px solid #ff9800;
        `;
      case 'info':
      default:
        return css`
          background-color: rgba(33, 150, 243, 0.95);
          color: white;
          border-left: 4px solid #2196f3;
        `;
    }
  }}
  
  @media (max-width: 768px) {
    left: 20px;
    right: 20px;
    min-width: auto;
  }
`;

const ToastContent = styled.div`
  flex: 1;
  font-size: 0.95rem;
  line-height: 1.4;
`;

const ToastTitle = styled.div`
  font-weight: 600;
  margin-bottom: 4px;
`;

const ToastMessage = styled.div`
  opacity: 0.95;
`;

const ToastReference = styled.div`
  font-size: 0.85rem;
  margin-top: 8px;
  opacity: 0.9;
  font-weight: 500;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: currentColor;
  cursor: pointer;
  font-size: 1.2rem;
  padding: 0;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  opacity: 0.8;
  transition: opacity 0.2s;
  
  &:hover {
    opacity: 1;
    background-color: rgba(255, 255, 255, 0.1);
  }
`;

const ProgressBar = styled.div<{ $duration: number }>`
  position: absolute;
  bottom: 0;
  left: 0;
  height: 3px;
  background-color: rgba(255, 255, 255, 0.7);
  border-radius: 0 0 var(--border-radius-sm) var(--border-radius-sm);
  animation: progress ${props => props.$duration}ms linear;
  
  @keyframes progress {
    from {
      width: 100%;
    }
    to {
      width: 0%;
    }
  }
`;

const Toast: React.FC<ToastProps> = ({
  message,
  type = 'info',
  duration = 5000,
  onClose,
  isVisible,
  bookingId
}) => {
  const [shouldRender, setShouldRender] = useState(isVisible);

  useEffect(() => {
    if (isVisible) {
      setShouldRender(true);
      const timer = setTimeout(() => {
        onClose();
      }, duration);

      return () => clearTimeout(timer);
    } else {
      const timer = setTimeout(() => {
        setShouldRender(false);
      }, 300); // Durée de l'animation de sortie
      
      return () => clearTimeout(timer);
    }
  }, [isVisible, duration, onClose]);

  if (!shouldRender) return null;

  const getTitle = () => {
    switch (type) {
      case 'success':
        return '✅ Demande envoyée !';
      case 'error':
        return '❌ Erreur';
      case 'warning':
        return '⚠️ Attention';
      case 'info':
      default:
        return 'ℹ️ Information';
    }
  };

  return (
    <ToastContainer $type={type} $isVisible={isVisible}>
      <ToastContent>
        <ToastTitle>{getTitle()}</ToastTitle>
        <ToastMessage>{message}</ToastMessage>
        {bookingId && (
          <ToastReference>
            Numéro de référence : #{bookingId}
          </ToastReference>
        )}
      </ToastContent>
      <CloseButton onClick={onClose} aria-label="Fermer">
        ×
      </CloseButton>
      {isVisible && <ProgressBar $duration={duration} />}
    </ToastContainer>
  );
};

export default Toast;
